// PATH: SaberpDeals/frontend/src/pages/NewDealPage.tsx
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogApi, type CatalogItem } from '@/lib/catalogApi';
import { customersApi, type Customer } from '@/lib/customersApi';
import { peopleApi, type Person } from '@/lib/peopleApi';
import { dealsApi, type DealType, type Billing } from '@/lib/dealsApi';

const DEAL_TYPES: DealType[] = ['New', 'Upsell', 'Renewal', 'Support'];

interface LineRow { key: string; catalogItemId: number | null; itemName: string; category: string; billingType: Billing; qty: number; unitPrice: number; lineDiscount: number; }
interface Assign { key: string; personId: number | ''; share: number; }

let seq = 0;
const nextKey = () => `k${seq++}`;

function money(n: number, cur: string) {
    const sym = cur === 'USD' ? '$' : 'IQD ';
    const dp = cur === 'USD' ? 2 : 0;
    return sym + (n || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function NewDealPage() {
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [people, setPeople] = useState<Person[]>([]);

    const [customerId, setCustomerId] = useState<number | ''>('');
    const [dealType, setDealType] = useState<DealType>('New');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [currency, setCurrency] = useState<'USD' | 'IQD'>('USD');
    const [lines, setLines] = useState<LineRow[]>([]);
    const [dealDiscount, setDealDiscount] = useState(0);
    const [split, setSplit] = useState({ company: 40, owner: 30, sales: 20, technical: 10 });
    const [salesPeople, setSalesPeople] = useState<Assign[]>([]);
    const [techPeople, setTechPeople] = useState<Assign[]>([]);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            const [c, cu, p] = await Promise.all([catalogApi.list(), customersApi.list(), peopleApi.list()]);
            setCatalog(c.filter((x) => x.isActive));
            setCustomers(cu);
            setPeople(p.filter((x) => x.isActive));
        })();
    }, []);

    useEffect(() => {
        dealsApi.defaults(dealType).then(setSplit).catch(() => { /* keep current */ });
    }, [dealType]);

    useEffect(() => {
        const c = customers.find((x) => x.id === customerId);
        if (c) setCurrency(c.defaultCurrency);
    }, [customerId, customers]);

    // Catalog grouped by category, for the <optgroup> dropdown.
    const catByGroup = useMemo(() => ({
        Package: catalog.filter((c) => c.category === 'Package'),
        License: catalog.filter((c) => c.category === 'License'),
        Service: catalog.filter((c) => c.category === 'Service'),
    }), [catalog]);

    // Ids already chosen on other lines — filtered out of each dropdown so the same
    // item can't be picked twice.
    function takenExcept(key: string): Set<number> {
        const s = new Set<number>();
        for (const l of lines) if (l.key !== key && l.catalogItemId != null) s.add(l.catalogItemId);
        return s;
    }

    function addLine() {
        setLines([...lines, { key: nextKey(), catalogItemId: null, itemName: '', category: '', billingType: 'OneTime', qty: 1, unitPrice: 0, lineDiscount: 0 }]);
    }
    function pickCatalog(row: LineRow, id: number) {
        const item = catalog.find((c) => c.id === id);
        setLines(lines.map((l) => l.key === row.key
            ? item
                ? { ...l, catalogItemId: id, itemName: item.name, category: item.category, billingType: item.billingType, unitPrice: item.defaultPrice, qty: 1 }
                : { ...l, catalogItemId: null, category: '' }
            : l));
    }
    function setLine(key: string, patch: Partial<LineRow>) { setLines(lines.map((l) => (l.key === key ? { ...l, ...patch } : l))); }
    function removeLine(key: string) { setLines(lines.filter((l) => l.key !== key)); }

    const calc = useMemo(() => {
        let oneTime = 0, recurring = 0;
        for (const l of lines) {
            const lt = Math.max(0, l.qty * l.unitPrice - l.lineDiscount);
            if (l.billingType === 'Recurring') recurring += lt; else oneTime += lt;
        }
        const base = Math.max(0, oneTime - dealDiscount);
        const total = base + recurring;
        const company = Math.round(base * split.company) / 100 + recurring;
        const owner = Math.round(base * split.owner) / 100;
        const salesPool = Math.round(base * split.sales) / 100;
        const techPool = Math.round(base * split.technical) / 100;
        const dist = (arr: Assign[], pool: number) => {
            const sum = arr.reduce((s, a) => s + (a.share || 0), 0);
            return arr.map((a) => ({ ...a, amt: sum > 0 ? Math.round(pool * a.share / sum * 100) / 100 : 0 }));
        };
        return { oneTime, recurring, base, total, company, owner, salesPool, techPool,
            sales: dist(salesPeople, salesPool), tech: dist(techPeople, techPool) };
    }, [lines, dealDiscount, split, salesPeople, techPeople]);

    const splitSum = split.company + split.owner + split.sales + split.technical;
    const canSave = customerId !== '' && lines.length > 0 && splitSum === 100 && calc.total > 0;

    async function save() {
        if (!canSave) return;
        setSaving(true); setError(null);
        try {
            const { id } = await dealsApi.create({
                customerId: Number(customerId), dealType, date, currency, dealDiscount, split,
                lines: lines.map((l) => ({ catalogItemId: l.catalogItemId, itemName: l.itemName || '(item)', billingType: l.billingType, qty: l.qty, unitPrice: l.unitPrice, lineDiscount: l.lineDiscount })),
                salesPeople: salesPeople.filter((a) => a.personId !== '').map((a) => ({ personId: Number(a.personId), share: a.share })),
                technicalPeople: techPeople.filter((a) => a.personId !== '').map((a) => ({ personId: Number(a.personId), share: a.share })),
            });
            navigate(`/deals?highlight=${id}`);
        } catch (e: any) {
            setError(e?.response?.data?.error ?? 'Could not save the deal.');
        } finally { setSaving(false); }
    }

    const fLabel = 'block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5';
    const fInput = 'w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2';
    const mini = 'text-sm bg-card border border-line-2 rounded-lg px-2 py-2 outline-none focus:border-forest-2';

    return (
        <div className="mt-6">
            <div className="mb-4">
                <div className="font-disp text-[21px] font-semibold tracking-tight">Record a deal</div>
                <div className="text-[13px] text-ink-3 mt-0.5">Pick the customer and type, add what's sold, then set the split. Percentages default by type and stay editable.</div>
            </div>

            {error && <div className="mb-4 rounded-lg bg-clay-soft border border-clay/30 px-4 py-3 text-sm text-clay">{error}</div>}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
                {/* form */}
                <div className="bg-card border border-line rounded-2xl p-6">
                    <div className="flex gap-3 mb-4">
                        <div className="flex-1">
                            <label className={fLabel}>Customer</label>
                            <select className={fInput} value={customerId} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')}>
                                <option value="">Select…</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div style={{ flex: '0 0 150px' }}>
                            <label className={fLabel}>Sale date</label>
                            <input type="date" className={fInput} value={date} onChange={(e) => setDate(e.target.value)} />
                        </div>
                    </div>

                    <label className={fLabel}>Deal type</label>
                    <div className="flex gap-1.5 flex-wrap mb-4">
                        {DEAL_TYPES.map((t) => (
                            <button key={t} onClick={() => setDealType(t)}
                                className={`text-[12.5px] font-mono px-3 py-2 rounded-lg border ${dealType === t ? 'bg-forest text-white border-forest' : 'bg-card text-ink-2 border-line-2'}`}>
                                {t}
                            </button>
                        ))}
                    </div>

                    <label className={fLabel}>Currency</label>
                    <div className="flex gap-1.5 mb-2">
                        {(['USD', 'IQD'] as const).map((c) => (
                            <button key={c} onClick={() => setCurrency(c)}
                                className={`text-[12.5px] font-mono px-4 py-2 rounded-lg border ${currency === c ? 'bg-forest text-white border-forest' : 'bg-card text-ink-2 border-line-2'}`}>
                                {c}
                            </button>
                        ))}
                    </div>

                    {/* items */}
                    <div className="flex items-center gap-3 mt-6 mb-3">
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-forest">Items sold</span>
                        <span className="flex-1 h-px bg-line" />
                    </div>
                    {lines.length > 0 && (
                        <div className="grid grid-cols-[1fr_52px_84px_72px_30px] gap-2 text-[10px] font-semibold uppercase tracking-wide text-ink-3 px-1 mb-1.5">
                            <span>Item</span><span className="text-center">Qty</span><span className="text-right">Unit</span><span className="text-right">Disc</span><span></span>
                        </div>
                    )}
                    {lines.map((l) => {
                        const taken = takenExcept(l.key);
                        const isLicense = l.category === 'License';
                        return (
                            <div key={l.key} className="grid grid-cols-[1fr_52px_84px_72px_30px] gap-2 items-center mb-2">
                                <select className={mini} value={l.catalogItemId ?? ''} onChange={(e) => e.target.value ? pickCatalog(l, Number(e.target.value)) : setLine(l.key, { catalogItemId: null, category: '' })}>
                                    <option value="">Pick item…</option>
                                    {catByGroup.Package.length > 0 && (
                                        <optgroup label="Packages">
                                            {catByGroup.Package.filter((c) => !taken.has(c.id) || c.id === l.catalogItemId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                    {catByGroup.License.length > 0 && (
                                        <optgroup label="Licenses">
                                            {catByGroup.License.filter((c) => !taken.has(c.id) || c.id === l.catalogItemId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                    {catByGroup.Service.length > 0 && (
                                        <optgroup label="Services">
                                            {catByGroup.Service.filter((c) => !taken.has(c.id) || c.id === l.catalogItemId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </optgroup>
                                    )}
                                </select>
                                <input className={`${mini} text-center ${!isLicense ? 'opacity-50 cursor-not-allowed' : ''}`} type="number" min={1}
                                    value={l.qty} disabled={!isLicense}
                                    title={isLicense ? '' : 'Quantity applies to licenses only'}
                                    onChange={(e) => setLine(l.key, { qty: parseFloat(e.target.value) || 1 })} />
                                <input className={`${mini} text-right`} type="number" min={0} value={l.unitPrice} onChange={(e) => setLine(l.key, { unitPrice: parseFloat(e.target.value) || 0 })} />
                                <input className={`${mini} text-right`} type="number" min={0} value={l.lineDiscount} onChange={(e) => setLine(l.key, { lineDiscount: parseFloat(e.target.value) || 0 })} />
                                <button onClick={() => removeLine(l.key)} className="h-9 border border-line-2 rounded-lg text-ink-3 hover:text-clay hover:border-clay">×</button>
                            </div>
                        );
                    })}
                    <button onClick={addLine} className="w-full border border-dashed border-line-2 rounded-lg text-[13px] font-medium text-ink-2 py-2.5 hover:border-forest-2 hover:text-forest">+ Add item</button>

                    <div className="flex justify-between items-center mt-3 text-[13px] text-ink-2">
                        <span>Deal-level discount</span>
                        <input className={`${mini} text-right w-28`} type="number" min={0} value={dealDiscount} onChange={(e) => setDealDiscount(parseFloat(e.target.value) || 0)} />
                    </div>

                    {/* split */}
                    <div className="flex items-center gap-3 mt-6 mb-3">
                        <span className="font-mono text-[11px] font-semibold uppercase tracking-widest text-forest">How it splits</span>
                        <span className="flex-1 h-px bg-line" />
                        <span className={`font-mono text-[12px] font-semibold px-2.5 py-1 rounded-xl ${splitSum === 100 ? 'bg-forest-soft text-forest' : 'bg-clay-soft text-clay'}`}>
                            {splitSum === 100 ? '100%' : `${splitSum}% — need 100`}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                        {([['company', 'Company'], ['owner', 'Owner (you)'], ['sales', 'Sales pool'], ['technical', 'Technical pool']] as const).map(([k, label]) => (
                            <div key={k} className="bg-paper border border-line rounded-xl px-3 py-2.5">
                                <label className="text-[11px] font-semibold text-ink-2 block mb-1">{label}</label>
                                <div className="flex items-center gap-1">
                                    <input type="number" min={0} max={100} className="w-full bg-card border border-line-2 rounded-lg px-2 py-1.5 font-mono text-sm outline-none focus:border-forest-2"
                                        value={(split as any)[k]} onChange={(e) => setSplit({ ...split, [k]: parseFloat(e.target.value) || 0 })} />
                                    <span className="text-ink-3 text-sm font-mono">%</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* people */}
                    <PeopleBlock title="Sales — who sold it" dotClass="bg-forest-2" pool={calc.salesPool} currency={currency}
                        people={people} rows={salesPeople} setRows={setSalesPeople} />
                    <PeopleBlock title="Technical — who set it up" dotClass="bg-amber2" pool={calc.techPool} currency={currency}
                        people={people} rows={techPeople} setRows={setTechPeople} />

                    <div className="flex gap-3 mt-6">
                        <button disabled={!canSave || saving} onClick={save}
                            className="bg-forest text-paper font-semibold text-sm rounded-lg px-6 py-3 hover:bg-forest-2 disabled:opacity-50">
                            {saving ? 'Saving…' : 'Save deal'}
                        </button>
                    </div>
                </div>

                {/* live payout */}
                <div className="lg:sticky lg:top-5">
                    <div className="bg-ink text-paper rounded-2xl px-6 py-5">
                        <div className="font-mono text-[10.5px] uppercase tracking-widest text-forest-soft/70">Deal total</div>
                        <div className="font-disp text-4xl font-semibold mt-1">{calc.total > 0 ? money(calc.total, currency) : '—'}</div>
                        {calc.recurring > 0 && (
                            <div className="font-mono text-[11.5px] text-paper/60 mt-2">{money(calc.base, currency)} one-time · {money(calc.recurring, currency)} recurring/yr</div>
                        )}
                    </div>

                    {calc.total > 0 && (
                        <div className="flex flex-col gap-2 mt-3">
                            <PayNode cls="border-l-ink-3" name="Company" role="off the top + recurring" amt={calc.company} cur={currency} dot="#788a82" />
                            <PayNode cls="border-l-plum bg-plum-soft" name="Owner (you)" role="off the top" amt={calc.owner} cur={currency} dot="#4a3a7a" />
                            {calc.sales.map((s, i) => {
                                const nm = people.find((p) => p.id === s.personId)?.name ?? '(unassigned)';
                                return <PayNode key={`s${i}`} cls="border-l-forest-2 bg-forest-soft" name={nm} role="sales" amt={s.amt} cur={currency} dot="#137a5e" />;
                            })}
                            {calc.sales.length === 0 && calc.salesPool > 0 && <PayNode cls="border-l-forest-2 bg-forest-soft" name="(no salesperson)" role="sales pool" amt={calc.salesPool} cur={currency} dot="#137a5e" />}
                            {calc.tech.map((s, i) => {
                                const nm = people.find((p) => p.id === s.personId)?.name ?? '(unassigned)';
                                return <PayNode key={`t${i}`} cls="border-l-amber2 bg-amber2-soft" name={nm} role="technical" amt={s.amt} cur={currency} dot="#8a5a12" />;
                            })}
                            {calc.tech.length === 0 && calc.techPool > 0 && <PayNode cls="border-l-amber2 bg-amber2-soft" name="(no implementer)" role="technical pool" amt={calc.techPool} cur={currency} dot="#8a5a12" />}
                        </div>
                    )}
                    <p className="text-[11.5px] text-ink-3 mt-3 leading-relaxed">
                        Commission is on the one-time portion; recurring is credited to the company on top of its share — so Company shows more than Owner at equal %. Frozen when saved.
                    </p>
                </div>
            </div>
        </div>
    );
}

function PeopleBlock({ title, dotClass, pool, currency, people, rows, setRows }: {
    title: string; dotClass: string; pool: number; currency: string;
    people: Person[]; rows: Assign[]; setRows: (r: Assign[]) => void;
}) {
    let s = 0; const key = () => `a${Date.now()}${s++}`;
    const money2 = (n: number) => (currency === 'USD' ? '$' : 'IQD ') + (n || 0).toLocaleString('en-US', { minimumFractionDigits: currency === 'USD' ? 2 : 0, maximumFractionDigits: currency === 'USD' ? 2 : 0 });
    // people already picked in this block, so the same person can't be added twice here
    const taken = new Set(rows.map((r) => r.personId).filter((x) => x !== ''));
    return (
        <div className="mt-4 bg-paper border border-line rounded-xl px-4 py-3.5">
            <div className="flex justify-between items-center mb-2.5">
                <span className="text-[12.5px] font-semibold text-ink-2 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${dotClass}`} />{title}
                </span>
                {pool > 0 && <span className="font-mono text-[13px] font-semibold text-forest">{money2(pool)}</span>}
            </div>
            {rows.map((r, i) => (
                <div key={r.key} className="grid grid-cols-[1fr_70px_30px] gap-2 items-center mb-2">
                    <select className="text-sm bg-card border border-line rounded-lg px-2.5 py-2 outline-none" value={r.personId}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, personId: e.target.value ? Number(e.target.value) : '' } : x)))}>
                        <option value="">Pick person…</option>
                        {people.filter((p) => !taken.has(p.id) || p.id === r.personId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input type="number" min={0} className="text-sm bg-card border border-line rounded-lg px-2.5 py-2 font-mono text-right outline-none"
                        value={r.share} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, share: parseFloat(e.target.value) || 0 } : x)))} />
                    <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="h-9 border border-line-2 rounded-lg text-ink-3 hover:text-clay">×</button>
                </div>
            ))}
            <button onClick={() => setRows([...rows, { key: key(), personId: '', share: rows.length ? 0 : 100 }])}
                className="w-full border border-dashed border-line-2 rounded-lg text-[12.5px] font-medium text-ink-2 py-2 hover:border-forest-2 hover:text-forest">+ Add</button>
        </div>
    );
}

function PayNode({ cls, name, role, amt, cur, dot }: { cls: string; name: string; role: string; amt: number; cur: string; dot: string; }) {
    const m = (cur === 'USD' ? '$' : 'IQD ') + (amt || 0).toLocaleString('en-US', { minimumFractionDigits: cur === 'USD' ? 2 : 0, maximumFractionDigits: cur === 'USD' ? 2 : 0 });
    return (
        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border border-line bg-card border-l-[3px] ${cls}`}>
            <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: dot }} />
                <span className="min-w-0">
                    <span className="text-[13.5px] font-semibold block truncate">{name}</span>
                    <span className="text-[10.5px] text-ink-3 uppercase tracking-wide font-mono">{role}</span>
                </span>
            </div>
            <span className="font-mono text-[13.5px] font-semibold">{m}</span>
        </div>
    );
}
